import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAuth as useCognitoAuth } from 'react-oidc-context';
import { preserveNextInPath, resolvePostLoginPath } from '../utils/authRedirect';
import { storePostLoginPath } from '../components/CognitoSessionBridge';
import toast from 'react-hot-toast';
import { ArrowRight } from 'lucide-react';

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const cognitoAuth = useCognitoAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const handleGoogleSignIn = async () => {
    try {
      storePostLoginPath(resolvePostLoginPath(searchParams, location.state?.from));
      await cognitoAuth.signinRedirect({
        extraQueryParams: {
          identity_provider: 'Google',
        },
      });
    } catch (err) {
      console.error('Google sign-in error:', err);
      toast.error(err?.message || 'Could not initiate Google sign-in. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ username, password });
      toast.success('Successfully logged in!');
      navigate(resolvePostLoginPath(searchParams, location.state?.from), { replace: true });
    } catch (err) {
      const data = err.response?.data
      if (data?.email_verification_required) {
        toast.error(data.error || 'Please verify your email before signing in.')
        const hint = username.includes('@') ? username : ''
        navigate(hint ? `/auth/verify-email?email=${encodeURIComponent(hint)}` : '/auth/verify-email')
        return
      }
      toast.error(data?.detail || data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex bg-bg-primary" style={{ minHeight: '100vh', width: '100%' }}>
      {/* Left Column: Form */}
      <div
        className="flex items-start sm:items-center justify-center"
        style={{
          flex: '1',
          padding: 'clamp(1.25rem, 4vw, var(--spacing-2xl))',
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-primary)',
          overflowY: 'auto',
        }}
      >
        <div style={{ width: '100%', maxWidth: '440px', paddingTop: '1rem', paddingBottom: '2rem' }}>
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-xs" style={{ marginBottom: 'var(--spacing-2xl)', textDecoration: 'none' }}>
            <div style={{
                background: 'var(--gradient-brand)',
                color: 'white',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '10px',
                fontWeight: 800,
                fontSize: '1.4rem',
            }}>L</div>
            <span style={{ fontWeight: 800, fontSize: '1.6rem', color: 'var(--color-text-primary)', letterSpacing: '-0.5px' }}>Learnly</span>
          </Link>

          <div>
            <h1 style={{ fontSize: '2.5rem', marginBottom: 'var(--spacing-xs)', lineHeight: 1.2 }}>Welcome back</h1>
            <p className="text-muted" style={{ fontSize: '1.1rem', marginBottom: 'var(--spacing-xl)' }}>
              Please enter your details to access your account.
            </p>
          </div>

          <form className="flex flex-col gap-lg" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-xs">
              <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Email or Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your email"
                style={{
                  padding: '14px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-primary)',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color var(--transition-fast)',
                  color: 'var(--color-text-primary)'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
            </div>

            <div className="flex flex-col gap-xs">
              <label style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  padding: '14px 16px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-primary)',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color var(--transition-fast)',
                  color: 'var(--color-text-primary)'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
            </div>

            <div className="flex justify-between items-center" style={{ marginTop: '-8px' }}>
              <label className="flex items-center gap-sm" style={{ cursor: 'pointer' }}>
                <input type="checkbox" style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary)' }} /> 
                <span className="text-muted" style={{ fontSize: '0.95rem' }}>Remember me</span>
              </label>
              <Link to="#" style={{ fontSize: '0.95rem', color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary shadow-md flex items-center justify-center gap-sm w-full"
              style={{ padding: '16px', fontSize: '1.1rem', marginTop: 'var(--spacing-md)' }}
            >
              {loading ? 'Signing In...' : (
                <>Sign in to your account <ArrowRight size={20} /></>
              )}
            </button>
          </form>

          <div style={{ margin: 'var(--spacing-lg) 0', textAlign: 'center', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: 'var(--color-border)' }} />
            <span style={{ position: 'relative', background: 'var(--color-bg-secondary)', padding: '0 1rem', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Or</span>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            aria-label="Continue with Google"
            className="btn btn-outline flex items-center justify-center gap-sm w-full"
            style={{
              marginBottom: '0.75rem',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div style={{ display: 'flex', gap: '0.75rem' }}>

            <Link to={preserveNextInPath('/auth/login-otp', searchParams)} className="btn btn-outline" style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem', padding: '0.7rem' }}>
              Login with OTP
            </Link>
            <Link to="/auth/verify-email" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center', fontSize: '0.9rem', padding: '0.7rem' }}>
              Verify Email
            </Link>
          </div>

          <p className="text-center text-muted" style={{ marginTop: 'var(--spacing-lg)', fontSize: '1rem' }}>
            Don't have an account? <Link to="/auth/register" style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}>Sign up</Link>
          </p>
        </div>
      </div>

      {/* Right column — brand-framed photo panel */}
      <div className="auth-photo-rail">
        <aside className="auth-photo-panel" aria-label="Learnly learning environment">
          <img
            className="auth-photo-panel__media"
            src="/auth_panel.webp"
            alt="Desk with notebook, books, and laptop by a window"
            loading="eager"
            decoding="async"
            fetchpriority="high"
            width="800"
            height="1000"
          />
          <div className="auth-photo-panel__scrub" aria-hidden="true" />
          <div className="auth-photo-panel__copy">
            <span className="auth-photo-panel__kicker">Jalusi Learnly</span>
            <h2>Structured learning for real organisations.</h2>
            <p>
              Courses, cohorts, and assessments in one calm workspace — built for campuses and teams that take capability seriously.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Login;
